import { useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';

type Props = {
  src: string;
  aspect?: number;
  onCancel: () => void;
  onCrop: (blob: Blob) => void;
};

const JPEG_QUALITY = 0.85;
const MAX_DIMENSION = 1920;

export default function ImageCropper({ src, aspect, onCancel, onCrop }: Props) {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [busy, setBusy] = useState(false);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    const initial = aspect
      ? centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height)
      : ({ unit: '%', x: 5, y: 5, width: 90, height: 90 } as Crop);
    setCrop(initial);
  }

  async function handleSave() {
    if (!completedCrop || !imgRef.current) return;
    setBusy(true);
    const blob = await renderCroppedJpeg(imgRef.current, completedCrop);
    setBusy(false);
    onCrop(blob);
  }

  return (
    <div className="space-y-3">
      <div className="bg-slate-100 rounded p-2 flex items-center justify-center max-h-[60vh] overflow-auto">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspect}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            onLoad={onImageLoad}
            className="max-w-full max-h-[55vh]"
          />
        </ReactCrop>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded text-sm"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!completedCrop || busy}
          className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
        >
          {busy ? t('common.working') : t('media.cropAndUpload')}
        </button>
      </div>
    </div>
  );
}

async function renderCroppedJpeg(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  let outWidth = crop.width * scaleX;
  let outHeight = crop.height * scaleY;

  // Skaler ned hvis output er over max-dimension (komprimering klient-side)
  const longest = Math.max(outWidth, outHeight);
  let scale = 1;
  if (longest > MAX_DIMENSION) {
    scale = MAX_DIMENSION / longest;
    outWidth *= scale;
    outHeight *= scale;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(outWidth);
  canvas.height = Math.round(outHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('toBlob returned null'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}
